import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle, WarningCircle, FloppyDisk, X, Users, Key, UserPlus,
} from "@phosphor-icons/react";

import StateCheckboxSelector from "@/components/shared/StateCheckboxSelector";
import SetPasswordDialog from "@/components/SetPasswordDialog";
import BuyerCriteriaRequestsPanel from "@/components/BuyerCriteriaRequestsPanel";
import { LeadStatusSettings } from "@/components/buyer/LeadStatusSettings";
import { BuyerDeliveryPlaces } from "@/components/delivery/DeliveryCopies";
import { LeadSourcesPanel } from "@/components/buyer/LeadSourcesPanel";
import { LoginAsBuyerButton, LoginAsUserButton } from "@/components/LoginAsButton";
import { EMPTY_NOTIFY, NotifyBuyerBox, notifyParams } from "@/components/NotifyBuyerBox";
import { BuyerDetailsEditor } from "@/components/buyer/BuyerDetailsEditor";
import { BuyerOverviewEditor } from "@/components/buyer/BuyerOverviewEditor";
import { DetailRequestsBanner } from "@/components/buyer/DetailRequestRow";
import { RuleCheck } from "@/components/buyer/RuleCheck";
import { useAuth } from "@/context/AuthContext";
import { SetupChecklist } from "@/components/buyersetup/SetupChecklist";
import { AcceptHours } from "@/components/buyersetup/AcceptHours";
import { BuyerTestLeads } from "@/components/buyer/BuyerTestLeads";
import { TestBuyerButton } from "@/components/buyer/TestBuyerButton";
import {
  AT_FAULT_INS_OPTIONS, ATTORNEY_OPTIONS, CASE_TYPES, FAULT_LEVELS, HITRUN_OPTIONS, INCIDENT_AGE_PRESETS,
  LEAD_TYPES, TRISTATE_OPTIONS,
} from "@/lib/buyerCriteriaOptions";

/**
 * Iter-61 — Manage Buyer / Intake Criteria full-page editor.
 *
 * Route: /clients/:id/manage
 *
 * ADR: single source of truth for a buyer's "what does a valid
 * lead look like" configuration. Sections are collapsible accordions
 * (mobile-friendly) but every save call goes through the SAME
 * `PATCH /api/organizations/:id` handler that the Clients table
 * already uses — no divergent write path.
 *
 * Zero-surprise contract: if the user does not touch a criterion
 * we leave it undefined in the payload, which the backend
 * interprets as "no restriction".
 */


// --------------------------------------------------------------------------
// Section wrapper
// --------------------------------------------------------------------------
function Section({ id, title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="brut-card" data-testid={`section-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex items-center justify-between border-b border-black bg-white text-left"
        data-testid={`section-toggle-${id}`}
      >
        <span className="font-chivo font-black uppercase tracking-tighter text-sm md:text-base">
          {title}
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
          {open ? "− Collapse" : "+ Expand"}
        </span>
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
}

function Radio({ testId, name, value, current, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer min-h-[44px]">
      <input
        type="radio"
        name={name}
        value={value}
        checked={current === value}
        onChange={() => onChange(value)}
        className="w-4 h-4"
        data-testid={`${testId}-${value}`}
      />
      <span className="text-xs">{label}</span>
    </label>
  );
}

function Checkbox({ testId, checked, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer min-h-[44px]">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5"
        data-testid={testId}
      />
      <span className="text-xs">{label}</span>
    </label>
  );
}

// --------------------------------------------------------------------------
// Main component
// --------------------------------------------------------------------------

export default function ManageBuyer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPerm, user } = useAuth();
  const [detailsKey, setDetailsKey] = useState(0);
  const onDetailApproved = () => {
    setDetailsKey((k) => k + 1);
    api.get(`/organizations/${id}`).then((r) => setBuyer(r.data)).catch(() => {});
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [buyer, setBuyer] = useState(null);
  const [criteria, setCriteria] = useState({});
  const [dirty, setDirty] = useState(false);
  const [notify, setNotify] = useState(EMPTY_NOTIFY);
  const [enforcement, setEnforcement] = useState({ enforced: [], not_enforced: [] });
  // Iter-71: client-org users the admin can reset the password on
  // without leaving this modal. `pwTarget` opens the shared
  // SetPasswordDialog; `null` = closed.
  const [teamMembers, setTeamMembers] = useState([]);
  const [pwTarget, setPwTarget] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);

  const loadTeam = React.useCallback(async () => {
    if (!id) return;
    setTeamLoading(true);
    try {
      const { data } = await api.get(`/employees?org_id=${id}`);
      setTeamMembers(data.employees || []);
    } catch { /* stay silent — team section is optional */ }
    finally { setTeamLoading(false); }
  }, [id]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [orgRes, enfRes] = await Promise.all([
          api.get(`/organizations/${id}`),
          api.get(`/organizations/${id}/intake-enforcement`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setBuyer(orgRes.data);
        setCriteria(orgRes.data?.intake_criteria || {});
        if (enfRes?.data) setEnforcement(enfRes.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load buyer");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const set = (key, value) => {
    setCriteria((c) => {
      const next = { ...c };
      if (value === "" || value === null || value === undefined
          || (Array.isArray(value) && value.length === 0)) {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
    setDirty(true);
  };

  const [caseOpen, setCaseOpen] = useState(false);   // used by CASE TYPES
  const toggleCase = (code) => {
    const cur = new Set(criteria.case_types || []);
    if (cur.has(code)) cur.delete(code);
    else cur.add(code);
    set("case_types", Array.from(cur).sort());
  };
  const toggleFault = (code) => {
    const cur = new Set(criteria.accepted_fault_levels || []);
    if (cur.has(code)) cur.delete(code);
    else cur.add(code);
    set("accepted_fault_levels", Array.from(cur).sort());
  };
  const toggleLeadType = (code) => {
    const cur = new Set(criteria.accepted_lead_types || []);
    if (cur.has(code)) cur.delete(code);
    else cur.add(code);
    set("accepted_lead_types", Array.from(cur).sort());
  };

  // Incident-age preset vs custom
  const [customAge, setCustomAge] = useState(false);
  useEffect(() => {
    if (criteria.max_incident_age_days == null) { setCustomAge(false); return; }
    const preset = INCIDENT_AGE_PRESETS.find(
      (p) => p.value === criteria.max_incident_age_days,
    );
    setCustomAge(!preset);
  }, [criteria.max_incident_age_days]);

  const save = async () => {
    setSaving(true);
    try {
      // Send criteria as-is (possibly empty {}). Backend clears the
      // field when we send an empty object.
      const payload = { intake_criteria: criteria };
      await api.patch(`/organizations/${id}`, payload, notifyParams(notify));
      toast.success("Buyer intake criteria saved");
      setDirty(false);
      setNotify(EMPTY_NOTIFY);
      // Re-fetch enforcement summary so the "auto-enforced vs
      // configured-only" panel refreshes.
      const enfRes = await api.get(`/organizations/${id}/intake-enforcement`).catch(() => null);
      if (enfRes?.data) setEnforcement(enfRes.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.message || JSON.stringify(detail) || "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const buyerAccepted = criteria.accepted_states || null;

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="label-mono">Loading buyer…</div>
      </div>
    );
  }
  if (!buyer) {
    return (
      <div className="p-4 md:p-8">
        <div className="label-mono">Buyer not found.</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 space-y-4" data-testid="manage-buyer-page">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => navigate("/clients")}
          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
          data-testid="manage-buyer-back"
        >
          <ArrowLeft size={10} weight="bold" /> Back to clients
        </button>
        <div>
          <div className="label-mono">Manage buyer</div>
          <h1 className="font-chivo font-black text-3xl md:text-4xl uppercase tracking-tighter leading-none">
            {buyer.name}
          </h1>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {["super_admin", "admin"].includes(user?.role) && <TestBuyerButton orgId={id} firm={buyer.name} />}
          <LoginAsBuyerButton url={`/admin/clients/${id}/logins`} testId="manage-buyer-login-as" />
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="bg-black text-white px-4 py-2 text-xs uppercase tracking-widest border border-black inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="manage-buyer-save"
          >
            <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
        {dirty && (
          <div className="w-full md:ml-auto md:w-96">
            <NotifyBuyerBox value={notify} onChange={setNotify} testId="manage-buyer-notify"
              preview={() => ({ kind: "rules", org_id: id, criteria })} />
          </div>
        )}
      </div>

      <SetupChecklist orgId={id} />

      {hasPerm("manage_clients") && (
        <Section id="details" title="Edit details" defaultOpen>
          <DetailRequestsBanner orgId={id} onDone={onDetailApproved} />
          <BuyerDetailsEditor key={detailsKey} orgId={id} onSaved={(d) => setBuyer((b) => ({ ...b, name: d.company_name,
            contact_email: d.contact_email, contact_phone: d.contact_phone }))} />
        </Section>
      )}

      <Section id="overview" title="Buyer overview" defaultOpen>
        {hasPerm("manage_clients") ? (
          <BuyerOverviewEditor buyer={buyer} orgId={id} onSaved={setBuyer} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div><span className="label-mono">Contact email:</span> {buyer.contact_email || "—"}</div>
            <div><span className="label-mono">Contact phone:</span> {buyer.contact_phone || "—"}</div>
            <div><span className="label-mono">Default price:</span> {buyer.price != null ? `$${buyer.price}` : "—"}</div>
            <div><span className="label-mono">Return window:</span> {buyer.return_window_days ?? 5} days</div>
            <div><span className="label-mono">Replacement cap:</span> {buyer.replacement_cap ?? "—"}</div>
            <div><span className="label-mono">Duplicate policy:</span> {buyer.duplicate_policy || "moderate"}</div>
            <div><span className="label-mono">Status:</span> {buyer.status}</div>
          </div>
        )}
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          The sections below set <b>which leads count as valid</b> for this buyer.
        </div>
      </Section>

      <Section id="lead-sources" title="Where their leads come from">
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          Every shared page and own page that can send this buyer leads, how many each one sent, and the thank-you page a visitor sees after.
        </div>
        <LeadSourcesPanel orgId={id} />
      </Section>

      <Section id="lead-status-updates" title="Lead status updates">
        <LeadStatusSettings orgId={id} />
      </Section>

      {hasPerm("edit_lead_information") && (
        <Section id="test-leads" title="Test leads" defaultOpen={false}>
          <BuyerTestLeads orgId={id} />
        </Section>
      )}

      {/* Iter-72: pending / resolved criteria-change requests from the
          buyer users linked to this client. Hidden entirely when the
          buyer has never submitted one. Approving here auto-applies the
          proposal via the same validated PATCH path this page uses. */}
      <BuyerCriteriaRequestsPanel
        clientOrgId={id}
        onApproved={async () => {
          // Refresh the criteria form + enforcement summary so the
          // just-applied values are visible without a page reload.
          try {
            const [orgRes, enfRes] = await Promise.all([
              api.get(`/organizations/${id}`),
              api.get(`/organizations/${id}/intake-enforcement`).catch(() => null),
            ]);
            setBuyer(orgRes.data);
            setCriteria(orgRes.data?.intake_criteria || {});
            if (enfRes?.data) setEnforcement(enfRes.data);
            setDirty(false);
          } catch { /* silent — the page will still show the old values, harmless */ }
        }}
      />

      <RuleCheck criteria={criteria} />

      <Section id="geographic" title="Geographic targeting">
        <div className="text-[11px] text-zinc-600 leading-relaxed">
          Buyer is only eligible for leads in these states. Empty = no geographic restriction.
          Individual delivery destinations can NARROW this list further but cannot broaden it.
        </div>
        <StateCheckboxSelector
          testId="buyer-state-selector"
          value={criteria.accepted_states || []}
          onChange={(next) => set("accepted_states", next)}
        />
        <details className="mt-2 text-xs">
          <summary className="cursor-pointer text-zinc-600 min-h-[44px]">Advanced — ZIP / county targeting</summary>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <div>
              <label className="label-mono">Accepted ZIPs (comma-separated)</label>
              <input
                className="w-full border border-black px-3 py-2 mt-1 font-mono text-xs"
                value={(criteria.accepted_zips || []).join(", ")}
                onChange={(e) => set(
                  "accepted_zips",
                  e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                )}
                data-testid="buyer-zips"
              />
            </div>
            <div>
              <label className="label-mono">Accepted counties (comma-separated)</label>
              <input
                className="w-full border border-black px-3 py-2 mt-1 font-mono text-xs"
                value={(criteria.accepted_counties || []).join(", ")}
                onChange={(e) => set(
                  "accepted_counties",
                  e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                )}
                data-testid="buyer-counties"
              />
            </div>
          </div>
        </details>
      </Section>

      <Section id="case-types" title="Case types">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
          {CASE_TYPES.map((c) => (
            <Checkbox
              key={c.code}
              testId={`case-type-${c.code}`}
              checked={(criteria.case_types || []).includes(c.code)}
              onChange={() => toggleCase(c.code)}
              label={c.label}
            />
          ))}
        </div>
      </Section>

      <Section id="qualification" title="Qualification rules">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-mono">Maximum incident age</div>
            <select
              className="w-full border border-black px-3 py-2 mt-1"
              data-testid="buyer-incident-age"
              value={
                customAge ? "custom" :
                criteria.max_incident_age_days == null ? "" :
                String(criteria.max_incident_age_days)
              }
              onChange={(e) => {
                const v = e.target.value;
                if (v === "custom") { setCustomAge(true); return; }
                if (v === "") { setCustomAge(false); set("max_incident_age_days", null); return; }
                setCustomAge(false);
                set("max_incident_age_days", Number(v));
              }}
            >
              {INCIDENT_AGE_PRESETS.map((p) => (
                <option key={String(p.value)} value={p.value ?? ""}>{p.label}</option>
              ))}
            </select>
            {customAge && (
              <input
                type="number"
                min="0"
                className="w-full border border-black px-3 py-2 mt-1 font-mono"
                placeholder="Days"
                value={criteria.max_incident_age_days ?? ""}
                onChange={(e) => set("max_incident_age_days", e.target.value === "" ? null : Number(e.target.value))}
                data-testid="buyer-incident-age-custom"
              />
            )}
            <div className="mt-2">
              <Checkbox
                testId="buyer-allow-ongoing-treatment"
                checked={criteria.allow_ongoing_treatment_older_cases}
                onChange={(v) => set("allow_ongoing_treatment_older_cases", v)}
                label="Allow older cases if ongoing / continuous treatment (stored — not yet auto-enforced)"
              />
            </div>
          </div>

          <div>
            <div className="label-mono">Physical injury required</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {TRISTATE_OPTIONS.map((o) => (
                <Radio key={o.value}
                  testId="buyer-injury"
                  name="buyer-injury"
                  value={o.value}
                  current={criteria.injury_required || "no_requirement"}
                  onChange={(v) => set("injury_required", v === "no_requirement" ? null : v)}
                  label={o.label} />
              ))}
            </div>
          </div>

          <div>
            <div className="label-mono">Medical treatment required</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {TRISTATE_OPTIONS.map((o) => (
                <Radio key={o.value}
                  testId="buyer-treatment"
                  name="buyer-treatment"
                  value={o.value}
                  current={criteria.treatment_required || "no_requirement"}
                  onChange={(v) => set("treatment_required", v === "no_requirement" ? null : v)}
                  label={o.label} />
              ))}
            </div>
            <div className="mt-2">
              <label className="label-mono">Treatment within X days (web pages don&apos;t ask this — leave empty)</label>
              <input
                type="number"
                min="0"
                className="w-full border border-black px-3 py-2 mt-1 font-mono text-xs"
                value={criteria.treatment_within_days ?? ""}
                onChange={(e) => set("treatment_within_days", e.target.value === "" ? null : Number(e.target.value))}
                data-testid="buyer-treatment-within-days"
              />
            </div>
          </div>

          <div>
            <div className="label-mono">Attorney representation</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {ATTORNEY_OPTIONS.map((o) => (
                <Radio key={o.value}
                  testId="buyer-attorney"
                  name="buyer-attorney"
                  value={o.value}
                  current={criteria.attorney_policy || "no_requirement"}
                  onChange={(v) => set("attorney_policy", v === "no_requirement" ? null : v)}
                  label={o.label} />
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="label-mono">Accepted fault levels</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 mt-1">
              {FAULT_LEVELS.map((f) => (
                <Checkbox
                  key={f.code}
                  testId={`buyer-fault-${f.code}`}
                  checked={(criteria.accepted_fault_levels || []).includes(f.code)}
                  onChange={() => toggleFault(f.code)}
                  label={f.label} />
              ))}
            </div>
            <div className="text-[11px] text-zinc-500 mt-1">
              Focused Funnel writes structured fault_level to <code>extra.fault_level</code>.
              Standard / Quiz forms map at_fault yes/no to <code>fully_at_fault</code> / <code>not_at_fault</code>.
            </div>
          </div>
        </div>
      </Section>

      <Section id="insurance" title="Insurance / evidence">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-mono">Police report filed (web pages don&apos;t ask this)</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {TRISTATE_OPTIONS.map((o) => (
                <Radio key={o.value}
                  testId="buyer-police"
                  name="buyer-police"
                  value={o.value}
                  current={criteria.police_report_required || "no_requirement"}
                  onChange={(v) => set("police_report_required", v === "no_requirement" ? null : v)}
                  label={o.label} />
              ))}
            </div>
          </div>
          <div>
            <div className="label-mono">Hit-and-run (web pages don&apos;t ask this)</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {HITRUN_OPTIONS.map((o) => (
                <Radio key={o.value}
                  testId="buyer-hitrun"
                  name="buyer-hitrun"
                  value={o.value}
                  current={criteria.hit_and_run || "no_requirement"}
                  onChange={(v) => set("hit_and_run", v === "no_requirement" ? null : v)}
                  label={o.label} />
              ))}
            </div>
          </div>
          <div>
            <div className="label-mono">Claimant insurance (web pages don&apos;t ask this)</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {TRISTATE_OPTIONS.map((o) => (
                <Radio key={o.value}
                  testId="buyer-claimant"
                  name="buyer-claimant"
                  value={o.value}
                  current={criteria.claimant_insured || "no_requirement"}
                  onChange={(v) => set("claimant_insured", v === "no_requirement" ? null : v)}
                  label={o.label} />
              ))}
            </div>
          </div>
          <div>
            <div className="label-mono">Other driver (at-fault party) insured</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {AT_FAULT_INS_OPTIONS.map((o) => (
                <Radio key={o.value}
                  testId="buyer-atfault-ins"
                  name="buyer-atfault-ins"
                  value={o.value}
                  current={criteria.at_fault_party_insured || "no_requirement"}
                  onChange={(v) => set("at_fault_party_insured", v === "no_requirement" ? null : v)}
                  label={o.label} />
              ))}
            </div>
          </div>
          <div>
            <div className="label-mono">Uninsured motorist (web pages don&apos;t ask this)</div>
            <div className="flex flex-wrap gap-3 mt-1">
              {HITRUN_OPTIONS.map((o) => (
                <Radio key={o.value}
                  testId="buyer-um"
                  name="buyer-um"
                  value={o.value}
                  current={criteria.uninsured_motorist || "no_requirement"}
                  onChange={(v) => set("uninsured_motorist", v === "no_requirement" ? null : v)}
                  label={o.label} />
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Checkbox
              testId="buyer-phone-verified"
              checked={criteria.phone_verified_required}
              onChange={(v) => set("phone_verified_required", v || null)}
              label="Phone verified required" />
            <Checkbox
              testId="buyer-trustedform"
              checked={criteria.trustedform_required}
              onChange={(v) => set("trustedform_required", v || null)}
              label="TrustedForm required" />
          </div>
        </div>
      </Section>

      <Section id="lead-types" title="Lead types">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1">
          {LEAD_TYPES.map((lt) => (
            <Checkbox
              key={lt.code}
              testId={`lead-type-${lt.code}`}
              checked={(criteria.accepted_lead_types || []).includes(lt.code)}
              onChange={() => toggleLeadType(lt.code)}
              label={lt.label} />
          ))}
        </div>
        <div className="text-[11px] text-zinc-500 mt-1">
          Iter-75 — the routing engine now checks incoming <code>lead_type</code>
          against this list. Affiliates that don&apos;t stamp a variant default
          to <code>web_lead</code>; leave this section empty (no boxes ticked)
          to accept every lead type.
        </div>
      </Section>

      <Section id="hours" title="Lead hours">
        <AcceptHours value={criteria.accept_schedule} onChange={(v) => set("accept_schedule", v)} />
      </Section>

      <Section id="special" title="Special instructions">
        <div>
          <textarea
            rows={4}
            className="w-full border border-black px-3 py-2 mt-1 text-sm"
            placeholder="Free-form notes for internal ops (never used for auto-rejection)"
            value={criteria.special_instructions || ""}
            onChange={(e) => set("special_instructions", e.target.value)}
            data-testid="buyer-special-instructions"
          />
        </div>
      </Section>

      <Section id="destinations" title="Delivery destinations">
        <BuyerDeliveryPlaces orgId={id} />
        <div className="mt-3 text-xs text-zinc-600">
          Use <b>Send test lead</b> on any place above to check it works and see exactly what it received.
          Retries, caps and state overrides live on the{" "}
          <a href="/delivery" className="underline">Delivery Destinations</a> page.
          Each destination can optionally OVERRIDE the buyer's state list — the override
          may narrow the buyer's accepted states but never broaden them.
        </div>
      </Section>

      <Section id="enforcement" title="Enforcement status">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-mono flex items-center gap-1">
              <CheckCircle size={12} weight="fill" className="text-emerald-700" /> Auto-enforced
            </div>
            <ul className="mt-1 space-y-1 text-xs" data-testid="enforcement-enforced">
              {enforcement.enforced.length ? enforcement.enforced.map((e) => (
                <li key={e.key} className="flex items-start gap-2">
                  <span className="text-emerald-700">✓</span>
                  <span>
                    <span className="font-bold">{e.label}</span>
                    {e.lead_field && (
                      <span className="text-zinc-500 font-mono ml-1">({e.lead_field})</span>
                    )}
                  </span>
                </li>
              )) : <li className="text-zinc-500">No criteria configured yet.</li>}
            </ul>
          </div>
          <div>
            <div className="label-mono flex items-center gap-1">
              <WarningCircle size={12} weight="fill" className="text-amber-700" /> Configured — not yet auto-enforced
            </div>
            <ul className="mt-1 space-y-1 text-xs" data-testid="enforcement-not-enforced">
              {enforcement.not_enforced.length ? enforcement.not_enforced.map((e) => (
                <li key={e.key} className="flex items-start gap-2">
                  <span className="text-amber-700">⚠</span>
                  <span>
                    <span className="font-bold">{e.label}</span>{" "}
                    <span className="text-zinc-500">— {e.reason}</span>
                  </span>
                </li>
              )) : <li className="text-zinc-500">All configured criteria are auto-enforced.</li>}
            </ul>
          </div>
        </div>
        <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">
          Criteria in the right-hand column are stored on the buyer for future enforcement.
          They never silently reject leads today — the routing engine records them as
          <code className="mx-1">unevaluated_criteria</code>
          when it cannot check them from currently-available lead fields.
        </div>
      </Section>

      {/* Iter-71: Team members list with per-user password reset. Rendered
           just above the mobile sticky save bar so it doesn't interrupt the
           intake-criteria flow but is still discoverable on the same page. */}
      <div className="mt-8 border-2 border-black bg-white" data-testid="manage-buyer-team">
        <div className="border-b-2 border-black bg-black text-white px-4 py-3 flex items-center gap-2">
          <Users size={14} weight="bold" />
          <div className="font-chivo font-black uppercase tracking-tight">
            Team members ({teamMembers.length})
          </div>
          <button
            type="button"
            onClick={() => navigate(`/employees/invite?org_id=${id}`)}
            data-testid="manage-buyer-invite-team"
            className="ml-auto border border-white bg-black text-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-white hover:text-black inline-flex items-center gap-1"
          >
            <UserPlus size={10} weight="bold" /> Invite
          </button>
        </div>
        <div>
          {teamLoading && (
            <div className="px-4 py-3 text-[11px] text-zinc-500">Loading…</div>
          )}
          {!teamLoading && teamMembers.length === 0 && (
            <div className="px-4 py-3 text-[11px] text-zinc-600" data-testid="manage-buyer-team-empty">
              No users on this client org yet. Click <b>Invite</b> to add one.
            </div>
          )}
          {!teamLoading && teamMembers.length > 0 && (
            <table className="w-full text-xs" data-testid="manage-buyer-team-table">
              <thead>
                <tr className="bg-zinc-100 uppercase tracking-widest text-[10px] border-b border-zinc-300">
                  <th className="text-left py-2 px-3">Email</th>
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3">Role</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="py-2 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {teamMembers.map((u) => (
                  <tr key={u.id} className="border-b border-zinc-200" data-testid={`manage-buyer-team-row-${u.id}`}>
                    <td className="py-2 px-3 font-mono text-[11px]">{u.email}</td>
                    <td className="py-2 px-3">{u.name}</td>
                    <td className="py-2 px-3">
                      <span className="border border-black bg-white px-1.5 py-0.5 text-[9px] uppercase tracking-widest">
                        {u.role}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <span className="border border-black bg-white px-1.5 py-0.5 text-[9px] uppercase tracking-widest">
                        {u.status}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center justify-end gap-1.5">
                      {u.role === "buyer" && !["deactivated", "invitation_expired"].includes(u.status) && (
                        <LoginAsUserButton u={u} testId={`manage-buyer-login-as-${u.id}`} />
                      )}
                      <button
                        onClick={() => setPwTarget(u)}
                        data-testid={`manage-buyer-set-pw-${u.id}`}
                        className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
                        title="Set a new password for this user"
                      >
                        <Key size={10} weight="bold" /> Password
                      </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {pwTarget && (
        <SetPasswordDialog
          user={pwTarget}
          onClose={() => setPwTarget(null)}
          onSaved={loadTeam}
          testIdPrefix="manage-buyer-set-pw"
        />
      )}

      {/* Mobile sticky save bar */}
      <div className="md:hidden fixed left-0 right-0 bottom-0 z-40 bg-white border-t border-black p-3 flex items-center gap-2" data-testid="manage-buyer-sticky-actions">
        <button
          onClick={() => navigate("/clients")}
          className="border border-black px-3 py-2 text-xs uppercase tracking-widest flex-1 inline-flex items-center gap-1 justify-center"
          data-testid="manage-buyer-cancel-mobile"
        >
          <X size={12} weight="bold" /> Cancel
        </button>
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="bg-black text-white px-4 py-2 text-xs uppercase tracking-widest border border-black flex-1 inline-flex items-center gap-1 justify-center disabled:opacity-40"
          data-testid="manage-buyer-save-mobile"
        >
          <FloppyDisk size={12} weight="bold" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
